/**
 * La fiche fantôme d'un dossier d'archives — M5 (retour Manon, mi-parcours).
 *
 * « Toute personne qui scannera le QR code verra qui a pris la boîte, et quand. »
 * Dossier sorti : « 📤 Sorti par … le … — retour prévu le … » (en rouge si le
 * retour est dépassé) + « Rendre ». Dossier en place : « Je le prends »
 * (pour soi, ou pour quelqu'un d'autre : cabinet, élu…).
 */
import { useState } from 'react';
import { api, ApiException } from '@/lib/api';
import type { ArchivesUnite } from '@roronoa-collectivites/shared-types';
import { FaireSignerButton } from '@/components/signature/FaireSignerButton';

const fr = (iso: string) => new Date(iso.length === 10 ? `${iso}T00:00:00Z` : iso)
  .toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric', timeZone: iso.length === 10 ? 'UTC' : 'Europe/Paris' });

export function FantomeDossier({ unite, onChange }: { unite: Pick<ArchivesUnite, 'id' | 'sortie'>; onChange: () => void }) {
  const [form, setForm] = useState(false);
  const [pour, setPour] = useState(''); const [motif, setMotif] = useState(''); const [retour, setRetour] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const act = async (f: () => Promise<unknown>) => {
    setErr(null);
    try { await f(); setForm(false); onChange(); } catch (e) { setErr(e instanceof ApiException ? e.payload.error : 'Action impossible'); }
  };
  const s = unite.sortie;

  if (s) {
    const retard = !!s.retourPrevu && s.retourPrevu < new Date().toISOString().slice(0, 10);
    return (
      <span className={`mt-0.5 block rounded-r-md border px-2 py-1 text-2xs ${retard ? 'border-urgent/50 text-urgent' : 'border-warning/50 text-ink'}`} role="status">
        📤 Sorti par <b>{s.emprunteur}</b> le {fr(s.sortieAt)}
        {s.motif ? ` — ${s.motif}` : ''}
        {s.retourPrevu ? ` · retour prévu le ${fr(s.retourPrevu)}${retard ? ' (dépassé)' : ''}` : ''}
        <button type="button" className="ml-2 font-medium text-accent hover:underline"
          onClick={() => void act(() => api.admin.archives.rendreUnite(unite.id))}>Rendre</button>
        {/* M1 — le bon de sortie se signe (signature simple). */}
        <span className="ml-2"><FaireSignerButton documentType="sortie_archive" documentId={s.id} signataireParDefaut={s.emprunteur} /></span>
        {err && <span className="ml-2 text-urgent">{err}</span>}
      </span>
    );
  }
  if (!form) {
    return (
      <button type="button" className="ml-2 text-2xs text-accent hover:underline" onClick={() => setForm(true)}>
        Je le prends
      </button>
    );
  }
  return (
    <span className="mt-1 flex flex-wrap items-center gap-1.5 text-2xs">
      <input className="h-7 rounded-r-md border border-border bg-surface px-2" placeholder="Pour quelqu'un d'autre ? (sinon : vous)"
        aria-label="Emprunteur" value={pour} maxLength={200} onChange={(e) => setPour(e.target.value)} />
      <input className="h-7 rounded-r-md border border-border bg-surface px-2" placeholder="Motif" aria-label="Motif"
        value={motif} maxLength={500} onChange={(e) => setMotif(e.target.value)} />
      <input type="date" className="h-7 rounded-r-md border border-border bg-surface px-2" aria-label="Retour prévu"
        value={retour} onChange={(e) => setRetour(e.target.value)} />
      <button type="button" className="font-medium text-accent hover:underline"
        onClick={() => void act(() => api.admin.archives.sortirUnite(unite.id, {
          emprunteurNom: pour.trim().length >= 2 ? pour.trim() : null, motif: motif.trim() || null, retourPrevu: retour || null,
        }))}>Confirmer la sortie</button>
      <button type="button" className="text-ink-subtle hover:underline" onClick={() => setForm(false)}>Annuler</button>
      {err && <span className="text-urgent">{err}</span>}
    </span>
  );
}
